<template>
    <div class="cc-channel-mix">
        <div class="cc-channel-mix__grid">
            <div class="cc-channel-mix__row cc-channel-mix__header">
                <div class="cc-channel-mix__label"></div>
                <div class="cc-channel-mix__track"></div>
                <div class="cc-channel-mix__count"># Recommendations</div>
                <div class="cc-channel-mix__share">% Recommendations</div>
            </div>

            <div v-for="channel in rows" :key="channel.code" class="cc-channel-mix__row">
                <div class="cc-channel-mix__label">{{ channel.label }}</div>
                <div class="cc-channel-mix__track">
                    <div class="cc-channel-mix__bar" :style="`width: ${channel.sharePercent}%; background: ${channel.color};`"></div>
                </div>
                <div class="cc-channel-mix__count">{{ channel.count }}</div>
                <div class="cc-channel-mix__share">{{ Math.round(channel.sharePercent) }}%</div>
            </div>
        </div>

        <div class="cc-channel-mix__accept">
            <div class="cc-channel-mix__accept-title">Accept rate by channel</div>
            <table class="cc-channel-mix__accept-table">
                <thead>
                    <tr>
                        <th class="text-left">Channel</th>
                        <th v-for="channel in acceptRates" :key="`channel-${channel.code}`" class="text-right">{{ channel.label }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="text-left cc-channel-mix__accept-label">Accept Rate</td>
                        <td v-for="channel in acceptRates" :key="`accept-${channel.code}`" class="text-right">{{ channel.acceptRate }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
// Digital is not in the data yet, but is listed so the mix shows it at zero.
const CHANNELS = [
    { code: "calls", label: "Calls", color: "#424242" },
    { code: "rep emails", label: "Rep Emails", color: "#a8a8a8" },
    { code: "digital", label: "Digital", color: "#a8a8a8" },
];

// Channel names are matched regardless of case and surrounding spaces.
const channelKey = (name) => String(name ?? "").trim().toLowerCase();

export default {
    name: "CommandCenterChannelMix",

    props: {
        // [{ channel_name, suggestions, accepted, share, accept_rate }], one per channel with data.
        channels: {
            type: Array,
            required: true,
        },
    },

    computed: {
        rows() {
            const byCode = Object.fromEntries(this.channels.map((channel) => [channelKey(channel.channel_name), channel]));
            const unlisted = this.channels
                .filter((channel) => !CHANNELS.some((known) => known.code === channelKey(channel.channel_name)))
                .map((channel) => ({ code: channelKey(channel.channel_name), label: channel.channel_name, color: "#a8a8a8" }));

            return [...CHANNELS, ...unlisted].map((channel) => {
                const data = byCode[channel.code];
                return {
                    ...channel,
                    count: Number(data?.suggestions) || 0,
                    sharePercent: Math.min(100, (Number(data?.share) || 0) * 100),
                    acceptRate: data?.accept_rate === null || data?.accept_rate === undefined ? "—" : `${Math.round(data.accept_rate * 100)}%`,
                };
            });
        },

        acceptRates() {
            return this.rows.filter((channel) => channel.count > 0);
        },
    },
};
</script>

<style scoped>
.cc-channel-mix {
    display: flex;
    flex-direction: column;
    width: 100%;
}

/* The rows share one grid so the text columns size to their widest cell
   (the header labels) and the bar track takes whatever width is left. */
.cc-channel-mix__grid {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr) max-content max-content;
    column-gap: 10px;
    align-items: center;
    font-size: 11px;
}

.cc-channel-mix__row {
    display: contents;
}

.cc-channel-mix__row > div {
    padding: 5px 0;
    white-space: nowrap;
}

.cc-channel-mix__label {
    color: #262626;
}

.cc-channel-mix__row > .cc-channel-mix__track {
    min-width: 0;
    height: 12px;
    padding: 0;
}

.cc-channel-mix__bar {
    height: 100%;
    transition: width 0.3s ease-in-out;
}

.cc-channel-mix__count,
.cc-channel-mix__share {
    color: #262626;
    font-weight: 700;
    text-align: right;
}

.cc-channel-mix__header > div {
    align-self: end;
    padding: 0 0 2px;
    font-size: 10px;
    line-height: 1.2;
}

.cc-channel-mix__header .cc-channel-mix__count,
.cc-channel-mix__header .cc-channel-mix__share {
    color: #5f5f5f;
    font-weight: 600;
}

.cc-channel-mix__accept {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #dedede;
    font-size: 11px;
}

.cc-channel-mix__accept-title {
    margin-bottom: 4px;
    color: #5f5f5f;
}

.cc-channel-mix__accept-table {
    border-collapse: collapse;
}

.cc-channel-mix__accept-table th {
    padding: 4px 0;
    border-bottom: 1px solid #dedede;
    color: #5f5f5f;
    font-weight: 600;
}

.cc-channel-mix__accept-table td {
    padding: 5px 0;
    color: #262626;
    font-weight: 700;
}

.cc-channel-mix__accept-table th:not(:last-child),
.cc-channel-mix__accept-table td:not(:last-child) {
    padding-right: 16px;
}

.cc-channel-mix__accept-table .cc-channel-mix__accept-label {
    color: #5f5f5f;
    font-weight: 600;
}
</style>
